import client, { type ClientOptions } from "../client";
import { AccountStatus, AttributeCatalogType, PaginationRequest, Status } from "src/types/commonTypes";
import internalClient from "../internalClient";
import { EMPTY_STRING } from "src/components/constant/constant";
import { buildGraphqlFieldsFromMap, graphqlSearchActivityFields } from "../../utils/graphqlFieldMaps";

export type ActivitySortField =
  | "created_at"
  | "activity_name"
  | "activity_purpose"
  | "accounting_status"
  | "receipt_available";

export interface TransactionRequest extends PaginationRequest {
  filter?: {
    organisation_id?: string;
    transactionType?: string;
    activityName?: string;
    activityNames?: string[];
    activityStatus?: string;
    activityStatuses?: string[];
    destinationWalletId?: string;
    walletIds?: string[];
    userIds?: string[];
    groupIds?: string[];
    created_at?: {
      min?: string;
      max?: string;
    };
    receipt_available?: boolean;
    xero_account_exist?: boolean;
    xero_bank_transaction_exist?: boolean;
    recurring_transaction_id?: string;
    expense_id?: string;
    reimbursement_id?: string;
    reference_activity_id?: string;
    transaction_user_id?: string;
    accounting_status?: AccountStatus;
    activity_needs_approval?: boolean;
    activity_needs_verification?: boolean;
    ppan?: string;
    ppans?: string[];
    card_id?: string;
    link_id?: string;
  };
  userFilter?: {
    email?: string;
  };
  walletFilter?: {
    displayId?: string;
  };
  sort?: string;
  sort_field?: {
    activity: ActivitySortField;
  };
  sort_by?: "asc" | "desc";
}

export const getAllTransaction = (
  data: TransactionRequest,
  fieldsObj: Record<string, unknown> = graphqlSearchActivityFields
) => {
  const fields = buildGraphqlFieldsFromMap(fieldsObj);
  const input: Record<string, unknown> = {
    from: data.from,
    size: data.size,
    sort: data?.sort,
    filter: {
      activity_filter: {
        organisation_id: data?.filter?.organisation_id,
        transaction_type: data?.filter?.transactionType,
        activity_name: data?.filter?.activityName,
        activity_names: data?.filter?.activityNames,
        activity_status: data?.filter?.activityStatus,
        activity_statuses: data?.filter?.activityStatuses,
        destination_wallet_id: data?.filter?.destinationWalletId,
        user_ids: data?.filter?.userIds,
        transaction_user_id: data?.filter?.transaction_user_id,
        wallet_ids: data?.filter?.walletIds,
        group_ids: data?.filter?.groupIds,
        created_at: data?.filter?.created_at,
        receipt_available: data?.filter?.receipt_available,
        xero_account_exist: data?.filter?.xero_account_exist,
        xero_bank_transaction_exist: data?.filter?.xero_bank_transaction_exist,
        recurring_transaction_id: data?.filter?.recurring_transaction_id,
        expense_id: data?.filter?.expense_id,
        reimbursement_id: data?.filter?.reimbursement_id,
        reference_activity_id: data?.filter?.reference_activity_id,
        accounting_status: data?.filter?.accounting_status,
        activity_needs_approval: data?.filter?.activity_needs_approval,
        activity_needs_verification: data?.filter?.activity_needs_verification,
        ppan: data?.filter?.ppan,
        ppans: data?.filter?.ppans,
        card_id: data?.filter?.card_id,
        link_id: data?.filter?.link_id
      },
      wallet_filter: {
        display_id: data?.walletFilter?.displayId
      }
    }
  };

  if (data?.sort_field) input.sort_field = data.sort_field;
  if (data?.sort_by) input.sort_by = data.sort_by;

  return client({
    query: `query SearchActivity($input: SearchInput) {
      search_activity(input: $input) {
        ${fields}
      }
    }`,
    variables: { input }
  });
};

export const getAllInternalTransaction = (data: TransactionRequest) => {
  return internalClient({
    query: `query SearchActivity($input: SearchInput) { 
      internal_search_activity(input: $input) { 
        total_pages 
        total_size 
        activity_listing {
          data
          activity_id
          activity_name
          activity_purpose
          activity_status
          process_status
          end_timestamp
          start_timestamp
          created_at
          owner_id
          receipt_available
          approval_number
          attributes {
            settled_amount
            settlement_delta
            settled_at
          }
        }
      }
    }`,
    variables: {
      input: {
        from: data.from,
        size: data.size,
        sort: data?.sort,
        filter: {
          activity_filter: {
            transaction_type: data?.filter?.transactionType,
            activity_name: data?.filter?.activityName,
            activity_status: data?.filter?.activityStatus,
            activity_statuses: data?.filter?.activityStatuses,
            destination_wallet_id: data?.filter?.destinationWalletId,
            user_ids: data?.filter?.userIds,
            wallet_ids: data?.filter?.walletIds,
            created_at: data?.filter?.created_at
          },
          wallet_filter: {
            display_id: data?.walletFilter?.displayId
          }
        }
      }
    }
  });
};

export type ReportColumn = {
  key: string;
  label: string;
};

export type GetReportColumnsRequest = {
  report_type: string;
};

export const getReportColumns = (data: GetReportColumnsRequest) => {
  return client({
    query: `query get_report_columns ($input: GetReportColumnsInput!) {
      get_report_columns(input: $input) {
        columns { key label }
      }
    }`,
    variables: { input: data }
  });
};

export type TransactionDownloadRequest = {
  filter?: {
    organisation_id?: string;
    wallet_ids?: string[];
    user_ids?: string[];
    group_ids?: string[];
    activity_name?: string;
    activity_names?: string[];
    activity_statuses?: string[];
    created_at?: {
      min?: string;
      max?: string;
    };
    transaction_user_id?: string;
    receipt_available?: boolean;
    xero_account_exist?: boolean;
    xero_bank_transaction_exist?: boolean;
    accounting_status?: AccountStatus;
  };
  timeZone?: string;
  columns?: string[];
  report_name?: string;
};

export const downloadTransaction = (data: TransactionDownloadRequest) => {
  return client({
    query: `query download_transactions ($input: DownloadReportInput) {
      download_transactions(input: $input) {
        report_id
      }
    }`,
    variables: {
      input: {
        filter: {
          activity_filter: data?.filter
        },
        payload: {
          filter: {
            activity_filter: data?.filter
          },
          from: 0,
          size: 10000
        },
        columns: data?.columns,
        timezone: data?.timeZone,
        report_name: data?.report_name || "transaction"
      }
    }
  });
};

export const downloadInternalTransaction = (data: TransactionDownloadRequest) => {
  return internalClient({
    query: `query download_transactions ($input: DownloadReportInput) {
      internal_download_transactions(input: $input) {
        report_id
      }
    }`,
    variables: {
      input: {
        filter: {
          activity_filter: data?.filter
        },
        payload: {
          filter: {
            activity_filter: data?.filter
          },
          from: 0,
          size: 10000
        },
        timezone: data?.timeZone,
        report_name: data?.report_name || "transaction"
      }
    }
  });
};

export const mockCardPurchase = (cardId: string, amount: number) => {
  return client({
    query: `mutation MockCardPurchase($input: MockCardPurchaseInput) {
      mock_card_purchase(input: $input) {
        code
        message
      }
    }`,
    variables: {
      input: {
        card_id: cardId,
        amount
      }
    }
  });
};

export const listNote = (transactionId: string) => {
  return client({
    query: `query ListNotes($input: ListNotesInput) { 
      list_notes(input: $input) { 
        note { 
          note_id 
          object_id 
          payload { 
            message 
            subject 
          } 
          updated_at
          created_by
        } 
      } 
    }`,
    variables: {
      input: {
        object_id: transactionId
      }
    }
  });
};

export type CreateNoteRequest = {
  objectId: string;
  objectType?: string;
  activityId?: string;
  note: string;
};

export const createNote = (data: CreateNoteRequest) => {
  return client({
    query: `mutation CreateNote($input: CreateNoteInput) {
      create_note(input: $input) {
        note_id
        payload {
          message
          subject
        }
      }
    }`,
    variables: {
      input: {
        payload: {
          message: data.note,
          subject: EMPTY_STRING
        },
        note_type: "BASIC",
        object_id: data.objectId,
        object_type: data.objectType,
        activity_id: data.activityId
      }
    }
  });
};

export const updateNote = (noteId: string, note: string) => {
  return client({
    query: `mutation UpdateNote($input: UpdateNoteInput) {
      update_note(input: $input) {
        note_id
        payload {
          message
          subject
        }
      }
    }`,
    variables: {
      input: {
        note_id: noteId,
        payload: {
          message: note,
          subject: EMPTY_STRING
        }
      }
    }
  });
};

export const transactionPhoto = (assetType: string, transactionId: string) => {
  return client({
    query: `query ListAssets($input: ListAssetsInput) {
      list_assets(input: $input) {
        next_token
        asset {
          asset_id
          attributes {
            bucket_name
            key
          }
          asset_type
          asset_status
        }
      }
    }`,
    variables: {
      input: {
        asset_type: assetType,
        object_id: transactionId
      }
    }
  });
};

export interface searchAssetRequest extends PaginationRequest {
  date?: {
    min?: string;
    max?: string;
  };
  fileTypes?: string[];
  fileIds?: string[];
}

export const searchAsset = (data: searchAssetRequest) => {
  return internalClient({
    query: `query SearchFile($input: SearchInput) {     
        search_file(input: $input) {        
          total_pages                
          total_size                
          files {                    
            file_id             
            file_type  
            process_status               
            attributes {     
              total_rows        
              key              
              bucket_name               
              date               
              e_tag
              original_filename
            }              
          }       
        }    
      }`,
    variables: {
      input: {
        filter: {
          file_filter: {
            date: data.date,
            file_types: data.fileTypes,
            file_ids: data.fileIds
          }
        },
        size: data.size,
        from: data.from
      }
    }
  });
};

export interface searchAssetContentRequest extends PaginationRequest {
  filterContentFilter: {
    file_ids: string[];
    settlement_status?: string[];
    process_status?: string[];
  };
}

export const searchAssetContent = (data: searchAssetContentRequest) => {
  return internalClient({
    query: `query SearchFileContent($input: SearchInput) {           
        search_file_content(input: $input) {        
          total_pages                
          total_size                
          file_content_list {    
            err_message   
            content_id             
            file_id             
            file_type        
            data    
            process_status 
            link_id
            link_type     
            attributes {                
              row_num  
              novatti_amount
              spendable_amount
              ppan
              approval_code
              settlement_status     
              balance_after_deposit
	            balance_before_deposit 
              wallet_before_deposit
            }              
          }       
        }    
      }`,
    variables: {
      input: {
        filter: {
          file_content_filter: data.filterContentFilter
        },
        size: data.size,
        from: data.from
      }
    }
  });
};

export const deletePhoto = (assetId: string) => {
  return client({
    query: `mutation DeleteAsset($input: DeleteAssetInput) {
      delete_asset(input: $input) {
        asset_id
        object_id
      }
    }`,
    variables: {
      input: {
        asset_id: assetId
      }
    }
  });
};

export interface getActivityRequest {
  activity_id?: string;
  link_id?: string;
  owner_id?: string;
}

export const getActivity = (data: getActivityRequest, options?: ClientOptions) => {
  return client({
    query: `query GetActivity($input: GetActivityInput) { 
      get_activity(input: $input) {
          data
          activity_id
          link_id
          activity_name
          activity_purpose
          activity_status
          process_status
          end_timestamp
          start_timestamp
          created_at
          owner_id
          receipt_available
          approval_number
          attributes {
            verified
            tagged_users
            expense_id
            settled_amount
            settlement_delta
            settled_at
          }
      }
    }`,
    variables: {
      input: data
    }
  }, options);
};

export interface ReverseCashTransactionRequest {
  link_id: string;
  reason: string;
}

export const reverseCashTransaction = (data: ReverseCashTransactionRequest) => {
  return client(
    {
      query: `mutation ReverseCashTransaction($input: ReverseCashTransactionInput) {
      reverse_cash_transaction(input: $input) {
        link_id
        owner_id
        wallet_id
        balance_after_txn
      }
    }`,
      variables: {
        input: data
      }
    },
    // The modal renders the failure inline; the generic toast would repeat it in raw backend prose.
    { suppressErrorToast: true }
  );
};

export const settleTransaction = (id: string) => {
  return internalClient({
    query: `mutation processContent($input: ProcessContentInput) {
      process_content(input: $input) {
        content_id
      }
    }`,
    variables: {
      input: {
        content_id: id,
        type: "SETTLEMENT"
      }
    }
  });
};

export const ignoreTransaction = (id: string, reason: string, note?: string) => {
  return internalClient({
    query: `mutation processContent($input: ProcessContentInput) {
      process_content(input: $input) {
        content_id
      }
    }`,
    variables: {
      input: {
        content_id: id,
        type: "IGNORE",
        reason,
        ...(note ? { note } : {})
      }
    }
  });
};

export const updateGstAmount = (id: string, price: number) => {
  return client({
    query: `mutation updateGst($input: UpdateActivityPriceComponentInput) {
      update_activity_price_components(input: $input) {
        activity_id
      }
    }`,
    variables: {
      input: {
        activity_id: id,
        price_components: {
          gst: price
        }
      }
    }
  });
};

export interface updateActivityRequest {
  activity_id: string;
  tagged_users: string[];
  verified: boolean;
  expense_id?: string;
}

export const updateActivity = (data: updateActivityRequest) => {
  return client({
    query: `mutation updateActivity($input: UpdateActivityInput) {
      update_activity(input: $input) {
        activity_id
      }
    }`,
    variables: {
      input: data
    }
  });
};

export interface adjustTransactionRequest {
  amount: number;
  merchantName: string;
  wallet_id: string;
  transaction_user_id: string;
  currency: string;
}

export const adjustTransaction = (data: adjustTransactionRequest) => {
  return client({
    query: `mutation adjustTransaction($input: CashTransactionInput) {
      adjust_cash_transaction(input: $input) {
        link_id
        owner_id 
      }
    }`,
    variables: {
      input: data
    }
  });
};

export const blockMerchant = (data: { wallet_ids: string[]; merchant_id: string }) => {
  return client({
    query: `mutation block_merchant($input: BlockMerchantInput) {
        block_merchant(input: $input) {
          preference_id
          is_cash_withdrawal_allowed
          spend_limit
          maximum_allowed_limit
          spend_frequency
          group_pay_id
          can_unload_group_pay
          blocked_merchant_ids
        }
      }`,
    variables: { input: data }
  });
};

export const getMerchant = (merchantIds: string[], options?: ClientOptions) => {
  return client(
    {
      query: `query GetMerchant($input: GetMerchantsInput) {
      get_merchants(input: $input) {
        merchant_id
        name
        mcc
        mcc_category
        external_id
      }
    }`,
      variables: { input: { external_ids: merchantIds } }
    },
    options
  );
};

export interface MccRequest extends PaginationRequest {
  filter?: {
    mcc_filter?: {
      mcc_categories?: string[];
      mcc?: string[];
    };
  };
}

export const searchMCC = (data: MccRequest) => {
  return client({
    query: `
    query SearchMCC($input: SearchInput) {
      search_mcc(input: $input) {
      total_pages 
      total_size 
        items {
          mcc_id
          title
          mcc
          mcc_category
        }
      }
    }`,
    variables: {
      input: {
        from: data?.from,
        size: data?.size,
        filter: data.filter
      }
    }
  });
};

export interface SaveActivityPreferenceRequest {
  activity_id: string;
  participated_users: {
    user_id: string;
    amount: number;
  }[];
}

export const saveActivityPreference = (data: SaveActivityPreferenceRequest) => {
  return client({
    query: `mutation SaveActivityPreference($input: ActivityPreferencesInput) {
      save_activity_preferences(input: $input) {
        activity_id
       }
    }`,
    variables: { input: data }
  });
};

export const getActivityPreferences = (activity_id: string) => {
  return client({
    query: `query GetActivityPreferences($input: GetActivityInput) {
      get_activity_preference(input: $input) {
        activity_id
        participated_users {
          user_id
          amount
        }
      }
    }`,
    variables: { input: { activity_id } }
  });
};

export type TransactionAllocation = {
  transaction_allocation_id: string;
  activity_id: string;
  user_id: string;
  status: string;
  allocated_amount: number;
  currency: string;
  gst_type: string;
  gst_rate: number;
  gst_amount: number;
  gl_code: string;
  category: string[];
  integration?: {
    xero?: {
      gl_code?: string;
      tax_type?: string;
      description?: string;
      category?: {
        xero_category_id?: string;
        xero_option_id?: string;
      }[];
    };
  };
  user: {
    user_id: string;
    email: string;
    attributes: {
      first_name: string;
      last_name: string;
    };
  };
};

export const getTransactionAllocations = (activity_id: string) => {
  return client({
    query: `query GetTransactionAllocations($input: GetTransactionAllocationsInput) {
      get_transaction_allocations(input: $input) {
        transaction_allocation_id
        activity_id
        user_id
        status
        allocated_amount
        currency
        gst_type
        gst_rate
        gst_amount
        gl_code
        category
        user {
          user_id
          email
          attributes {
            first_name
            last_name
          }
        }
        integration {
          xero {
            gl_code
            tax_type
            description
            category {
              xero_category_id
              xero_option_id
            }
          }
        }
      }
    }`,
    variables: { input: { activity_id } }
  });
};

export interface CreateTransactionAllocationRequest {
  activity_id: string;
  user_id: string;
  allocated_amount: number;
  currency: string;
  gst_type: string;
  gst_rate?: number;
  gst_amount?: number;
  gl_code?: string;
  category: string[];
  integration?: {
    xero?: {
      gl_code?: string;
      tax_type?: string;
      description?: string;
      category?: {
        xero_category_id?: string;
        xero_option_id?: string;
      }[];
    };
  };
}
export const createTransactionAllocation = (data: CreateTransactionAllocationRequest) => {
  return client({
    query: `mutation CreateTransactionAllocation($input: CreateTransactionAllocationInput) {
      create_transaction_allocation(input: $input) {
        transaction_allocation_id
        activity_id
        user_id
        status
        allocated_amount
        currency
        gst_type
        gst_rate
        gst_amount
        gl_code
        category
      }
    }`,
    variables: { input: data }
  });
};

export interface UpdateTransactionAllocationRequest {
  transaction_allocation_id: string;
  activity_id: string;
  user_id: string;
  allocated_amount: number;
  currency: string;
  gst_type: string;
  gst_rate?: number;
  gst_amount?: number;
  gl_code?: string;
  category: string[];
  status: "ACTIVE" | "INACTIVE";
  integration?: {
    xero?: {
      gl_code?: string;
      tax_type?: string;
      description?: string;
      category?: {
        xero_category_id?: string;
        xero_option_id?: string;
      }[];
    };
  };
}

export type CreateTransactionAllocationRowInput = Omit<CreateTransactionAllocationRequest, "activity_id">;

export const updateTransactionAllocation = (data: UpdateTransactionAllocationRequest) => {
  return client({
    query: `mutation UpdateTransactionAllocation($input: UpdateTransactionAllocationInput) {
      update_transaction_allocation(input: $input) {
        transaction_allocation_id
      }
    }`,
    variables: { input: data }
  });
};

export const createAccountingTransaction = (activity_id: string) => {
  return client({
    query: `mutation CreateAccountingTransaction($input: CreateAccountingTransactionInput) {
      create_accounting_transaction(input: $input) {
        accounting_transaction_id
        
      }
    }`,
    variables: { input: { activity_id } }
  });
};

export const getAccountTransaction = (activity_id: string) => {
  return client({
    query: `query GetAccountTransaction($input: GetAccountingTransactionInput) {
      get_accounting_transaction(input: $input) {
        accounting_transaction_id
        activity_id
        status
        tax_details {
          gst_amount
        }
        xero_bankTransaction {
          synced_at
        }
        custom_attributes
      }
    }`,
    variables: { input: { activity_id } }
  });
};

export interface UpdateAccountTransactionRequest {
  accounting_transaction_id: string;
  activity_id: string;
  status?: AccountStatus;
  reason?: string;
  tax_details?: {
    gst_amount: number;
  };
  custom_attributes?: Record<string, string>;
}

export const updateAccountTransaction = (data: UpdateAccountTransactionRequest) => {
  const { custom_attributes, ...rest } = data;
  const input: Record<string, unknown> = { ...rest };

  if (custom_attributes !== undefined) {
    input.custom_attributes = JSON.stringify(custom_attributes);
  }

  return client({
    query: `mutation UpdateAccountTransaction($input: UpdateAccountingTransactionInput) {
      update_accounting_transaction(input: $input) {
        accounting_transaction_id
        custom_attributes
      }
    }`,
    variables: { input }
  });
};

export type AttributeCatalogValueType = "STRING" | "NUMBER" | "DATE" | "ENUM";

export interface AttributeCatalogAttributes {
  applies_to?: string[];
  value_type?: AttributeCatalogValueType;
  options?: string[];
}

export interface AttributeCatalog {
  attribute_catalog_id: string;
  code: string;
  type: AttributeCatalogType;
  title: string;
  status: Status;
  organisation_id?: string;
  user_id?: string;
  wallet_id?: string;
  created_at?: string;
  updated_at?: string;
  version?: string;
  attributes?: AttributeCatalogAttributes;
}

export interface CreateAttributeCatalogRequest {
  code: string;
  type: AttributeCatalogType;
  title: string;
  attributes?: AttributeCatalogAttributes;
}

export const createAttributeCatalog = (data: CreateAttributeCatalogRequest) => {
  return client({
    query: `mutation CreateAttributeCatalog($input: CreateAttributeCatalogInput) {
      create_attribute_catalog(input: $input) {
        attribute_catalog_id
        code
        type
        title
        status
        organisation_id
        created_at
        updated_at
        version
        attributes {
          applies_to
          value_type
          options
        }
      }
    }`,
    variables: { input: data }
  });
};

export interface UpdateAttributeCatalogRequest {
  attribute_catalog_id: string;
  code: string;
  type: AttributeCatalogType;
  title: string;
  status: Status;
  attributes?: AttributeCatalogAttributes;
}

export const updateAttributeCatalog = (data: UpdateAttributeCatalogRequest) => {
  return client({
    query: `mutation UpdateAttributeCatalog($input: UpdateAttributeCatalogInput) {
      update_attribute_catalog(input: $input) {
        attribute_catalog_id
        code
        type
        title
        status
        organisation_id
        created_at
        updated_at
        version
        attributes {
          applies_to
          value_type
          options
        }
      }
    }`,
    variables: { input: data }
  });
};

export interface GetAttributeCatalogRequest extends PaginationRequest {
  attribute_catalog_filter: {
    code?: string;
    codes?: string[];
    type?: AttributeCatalogType;
    attribute_catalog_id?: string;
    attribute_catalog_ids?: string[];
    title?: string;
    status?: Status;
    organisation_id?: string;
    applies_to?: string;
  };
}

export const getAttributeCatalog = (data: GetAttributeCatalogRequest) => {
  return client({
    query: `query GetAttributeCatalog($input: SearchInput) {
      search_attribute_catalog(input: $input) {
        total_pages
        total_size
        items {
          attribute_catalog_id
          code
          type
          title
          status
          organisation_id
          user_id
          wallet_id
          created_at
          updated_at
          version
          attributes {
            applies_to
            value_type
            options
          }
        }
      }
    }`,
    variables: {
      input: {
        from: data.from,
        size: data.size,
        filter: {
          attribute_catalog_filter: data.attribute_catalog_filter
        }
      }
    }
  });
};

export type AccountingAuditOp =
  | "CREATE"
  | "SUBMIT"
  | "APPROVE"
  | "MARK_READY"
  | "REJECT"
  | "RESUBMIT"
  | "MARK_DRAFT";

export type AccountingAuditLogEntry = {
  audit_id: string;
  accounting_transaction_id: string;
  organisation_id: string;
  created_at: string;
  op: AccountingAuditOp;
  from_status?: AccountStatus | null;
  to_status?: AccountStatus | null;
  actor_id: string;
  actor_role?: string | null;
  caller: string;
  payload?: Record<string, unknown> | string | null;
  snapshot: {
    accounting_transaction_id?: string;
    activity_id?: string;
    status?: AccountStatus;
    display_id?: string;
    tax_details?: {
      gst_amount?: number;
    };
    custom_attributes?: Record<string, unknown> | string;
    xero_bankTransaction?: {
      synced_at?: string;
    };
  };
};

export const getAccountingHistory = (accounting_transaction_id: string) => {
  return client({
    query: `query GetAccountingHistory($input: GetAccountingHistoryInput) {
      get_accounting_history(input: $input) {
        audit_id
        accounting_transaction_id
        organisation_id
        created_at
        op
        from_status
        to_status
        actor_id
        actor_role
        caller
        payload
        snapshot {
          accounting_transaction_id
          activity_id
          status
          display_id
          tax_details {
            gst_amount
          }
          custom_attributes
          xero_bankTransaction {
            synced_at
          }
        }
      }
    }`,
    variables: { input: { accounting_transaction_id } }
  });
};
